// pages/Dashboard.js
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDashboard } from '../hooks/useDashboard';
import { AppointmentModal } from '../components/modals/AppointmentModal';
import { useCustomers } from '../context/CustomerContext';
import { CustomerModal } from '../components/modals/CustomerModal';
import { useServicesPG } from '../hooks/useServicesPG';
import { useAppointmentsPG } from '../hooks/useAppointmentsPG';
import { useTranslations } from '../hooks/useTranslations';
import { MetricsSection, TodayScheduleSection, WelcomeTextManager } from '../components/dashboard';
import { PendingApprovalsSection } from '../components/dashboard/PendingApprovalsSection';
import { ContentLayout, PageContainer } from '../components/layout';
import { useBusinessConfig } from '../context/BusinessConfigContext';
import UnpaidPaymentsList from '../components/appointments/UnpaidPaymentsList';
import { PaymentAPI } from '../services/api/paymentAPI';
import { useAuth } from '../context/AuthContext';
import { UserPlus, CalendarPlus } from 'lucide-react';
import { OverdueAppointmentsSection } from '../components/dashboard/DashboardSections';
import { WorkingHoursConfig } from '../components/schedule/WorkingHoursConfig';

export function Dashboard({ onNavigate }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [modalMode, setModalMode] = useState('edit');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [prefilledDate, setPrefilledDate] = useState('');
  const [overdueAppointments, setOverdueAppointments] = useState([]);
  const [unpaidAppointments, setUnpaidAppointments] = useState([]);
  const [todaysAppointments, setTodaysAppointments] = useState([]);
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(0);

  // Get real data from hooks
  const { config } = useBusinessConfig();
  const { customers, handleCustomerSubmit } = useCustomers();
  const { services } = useServicesPG();
  const {
    handleAppointmentSubmit: originalHandleAppointmentSubmit,
    invalidateCacheForDate,
    pendingAppointments,
    getPendingApprovals,
    pendingLoading,
    error,
    getAppointment,
    getOverdueAppointments,
    getUnpaidAppointments,
    getRecentAppointments
  } = useAppointmentsPG();
  const { user } = useAuth();
  const { business_name } = useBusinessConfig();
  const isShowPendingApprovals = config && config?.settings?.approval_workflow_enabled === true;

  useEffect(() => {
    if (isShowPendingApprovals) {
      getPendingApprovals()
    }
  }, [dashboardRefreshTrigger, isShowPendingApprovals])

  useEffect(() => {
    const loadOverdueAppointments = async () => {
      try {
        setOverdueLoading(true);
        const overdue = await getOverdueAppointments();
        const unpaid = await getUnpaidAppointments();
        const todays = await getRecentAppointments();
        setOverdueAppointments(overdue);
        setUnpaidAppointments(unpaid);
        setTodaysAppointments(todays);
      } catch (error) {
        console.error('Failed to load overdue appointments:', error);
      } finally {
        setOverdueLoading(false);
      }
    };

    loadOverdueAppointments();
  }, [getOverdueAppointments, getUnpaidAppointments, dashboardRefreshTrigger]);

  const {
    metrics,
    metricsLoading,
    metricsError,
    refreshMetrics,
  } = useDashboard();

  const t = useTranslations();

  const handleNewCustomer = () => {
    setIsCustomerModalOpen(true);
  };

  const handleAppointmentSubmitWithRefresh = async (appointmentData, mode = 'add') => {
    try {
      const result = await originalHandleAppointmentSubmit(appointmentData, mode);

      setDashboardRefreshTrigger(prev => prev + 1);

      return result;
    } catch (error) {
      throw error;
    }
  };

  const handleRefreshAppointment = async (appointmentId) => {
    try {
      console.log('Refreshing appointment after payment completion:', appointmentId);

      // Fetch fresh appointment data from API to get the date
      const apt = await getAppointment(appointmentId, true);

      // Invalidate the week cache for this appointment's date
      // This ensures Schedule will fetch fresh data when opened
      invalidateCacheForDate(apt.date);

      // Refresh dashboard to show updated data
      setDashboardRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error refreshing appointment:', error);
    }
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleNewAppointment = () => {
    setSelectedAppointment(null);
    const today = new Date().toISOString().split('T')[0];
    setPrefilledDate(today);
    setModalMode('add');
    setIsModalOpen(true);
    setDashboardRefreshTrigger(prev => prev + 1);
  };

  const handleRequestPayment = async (payment) => {
    try {
      // Only allow new payment links for failed or expired payments
      if (!['failed', 'expired'].includes(payment.status)) {
        console.warn('Cannot create new payment link for status:', payment.status);
        return;
      }

      console.log('Requesting new payment for:', payment.appointmentId);

      // Fetch full appointment data using appointmentId
      const appointment = await getAppointment(payment.appointmentId);

      if (!appointment) {
        throw new Error('לא ניתן למצוא את הפגישה');
      }

      // Create new payment link using full appointment data
      const paymentContext = PaymentAPI.generatePaymentContext(appointment, user.uid);
      const result = await PaymentAPI.createPaymentLink(paymentContext);

      console.log('New payment link created:', result);

      // Optional: Show success message
      // You can add toast notification here

    } catch (error) {
      console.error('Error requesting payment:', error);
      // Optional: Show error message
      // You can add toast notification here
    }
  };

  const handleCancelPayment = async (paymentId) => {
    try {
      console.log('Cancelling payment:', paymentId);

      if (!window.confirm('האם אתה בטוח שברצונך לבטל את בקשת התשלום?')) {
        return;
      }

      await PaymentAPI.cancelPaymentRequest(user.uid, paymentId);
      console.log('Payment cancelled successfully');

      // Optional: Show success message
      // You can add toast notification here

    } catch (error) {
      console.error('Error cancelling payment:', error);
      // Optional: Show error message
      // You can add toast notification here
    }
  };

  const handleCompleteAppointment = async (appointment) => {
    try {
      console.log('Marking appointment as complete:', appointment.id);

      // Update appointment status to 'completed'
      await originalHandleAppointmentSubmit(
        {
          ...appointment,
          status: 'completed'
        },
        'edit'
      );

      console.log('Appointment marked as complete successfully');

      // Note: We don't trigger a full dashboard refresh here
      // The OverdueAppointmentsSection handles optimistic updates
      // and the appointment is already removed from the UI
    } catch (error) {
      console.error('Error marking appointment as complete:', error);
      alert('שגיאה בעדכון הפגישה');
      throw error; // Re-throw so the optimistic update can be reverted
    }
  };

  const handleMarkAsPaid = async (appointment) => {
    try {
      await originalHandleAppointmentSubmit(
        {
          ...appointment,
          is_paid: true
        },
        'edit'
      );
    } catch (error) {
      console.error('Error marking appointment as paid:', error);
      alert('שגיאה בעדכון התשלום');
      throw error;
    }
  };

  return (
    <ContentLayout
      currentPage="dashboard"
      onNavigate={onNavigate}
      title={`${t.welcomeTitle} - ${business_name}`}
      description={t.welcomeSubtitle}
      primaryAction={{
        label: t.newAppointment,
        onClick: () => handleNewAppointment(),
        icon: <CalendarPlus className="w-4 h-4" />  // ✅ Correct
      }}
      secondaryActions={[
        {
          label: t.newCustomer,
          onClick: () => handleNewCustomer(),
          icon: <UserPlus className="w-4 h-4" />  // ✅ Correct
        }
      ]}
    >
      <PageContainer>
        {/* Metrics Cards */}
        <MetricsSection
          metrics={metrics}
          loading={metricsLoading}
          error={metricsError}
          onRefresh={refreshMetrics}
          t={t}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {isShowPendingApprovals && (
            <PendingApprovalsSection
              pendingAppointments={pendingAppointments}
              loading={pendingLoading}
              error={error}
              onAppointmentUpdate={handleAppointmentSubmitWithRefresh}
              onAppointmentClick={handleAppointmentClick}
              invalidateCacheForDate={invalidateCacheForDate}
            />
          )}
          
          {/* Today's Schedule - Independent loading */}
          <TodayScheduleSection
            schedule={todaysAppointments}
            loading={overdueLoading}
            error={error}
            onAppointmentClick={handleAppointmentClick}
            onNewAppointmentClick={handleNewAppointment}
            onNavigate={onNavigate}
            customers={customers}
            services={services}
            t={t}
          />

          {/* Unpaid payments - takes 1 column on large screens */}
          <div className="lg:col-span-1">
            <UnpaidPaymentsList
              onRequestPayment={handleRequestPayment}
              onCancelPayment={handleCancelPayment}
              onRefreshAppointment={handleRefreshAppointment}
            />
          </div>

          <OverdueAppointmentsSection
            appointments={overdueAppointments}
            unpaidAppointments={unpaidAppointments}
            loading={overdueLoading}
            error={error}
            onAppointmentClick={handleAppointmentClick}
            onCompleteAppointment={handleCompleteAppointment}
            onMarkAsPaid={handleMarkAsPaid}
            customers={customers}
            services={services}
            t={t}
          />
        </div>

        {/* Welcome Text Manager - Only show if approval workflow is enabled */}
        {isShowPendingApprovals && (
          <div className='mt-6 mb-6'>
            <WelcomeTextManager />
          </div>
        )}

        <div className='mt-6 mb-6'>
          <WorkingHoursConfig />
        </div>
      </PageContainer>

      <AppointmentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedAppointment(null);
          setModalMode('add');
        }}
        onSuccess={handleAppointmentSubmitWithRefresh}
        appointment={selectedAppointment}
        prefilledDate={prefilledDate}
        mode={modalMode}
        services={services}
      />

      <CustomerModal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        onSubmit={handleCustomerSubmit}
        mode="add"
      />
    </ContentLayout>
  );
}